import { Alert, Button, Col, DatePicker, Form, Input, InputNumber, Switch, Typography } from 'antd';
import { FormProps } from 'antd/lib/form';
import moment from 'moment'; // TODO: date-fns 使ってるので moment 使わないようにしたい
import React from 'react';
import { useHistory } from 'react-router-dom';

import { EmailTemplate } from '@/api-client/generated/models';
import { useGacha } from '@/hooks/context/GachaContext';
import { convertRateToPercentage } from '@/service/numericOperation';

const FORM_SETTINGS = {
  winningPercentage: {
    min: 0.01,
    max: 30,
    step: 0.001,
  },
};

export type FormValues = {
  name: string;
  encryptionPassword: string;
  upperTimes: number;
  winningPercentage: number;
  iosDownloadUrl?: string;
  androidDownloadUrl?: string;
  canRetry: boolean;
  retryInterval?: number;
  validPeriod?: moment.Moment[];
  gameTitle?: string;
  developerName?: string;
  iconImageUrl?: string;
  giftDetail?: string;
  gameIntroduction?: string;
  articleUrl?: string;
  gameReleaseTime?: string;
  officialSiteUrl?: string;
  releaseNotificationEmailTitle?: string;
  releaseNotificationEmailBody?: string;
  releaseNotificationEmailHtmlBody?: string;
  registrationNotificationEmailTitle?: string;
  registrationNotificationEmailBody?: string;
  registrationNotificationEmailHtmlBody?: string;
};

type GachaFormProps = {
  onFinish: (formValues: FormValues) => void;
  // eslint-disable-next-line react/require-default-props
  releaseEmailTemplate?: EmailTemplate;
  // eslint-disable-next-line react/require-default-props
  registrationEmailTemplate?: EmailTemplate;
};

const formItemLayout: FormProps = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 12 },
  },
};

const rangePickerDefaultTime: moment.Moment = moment('00:00:00', 'HH:mm:ss');

const GachaForm: React.FC<GachaFormProps> = ({ onFinish, releaseEmailTemplate, registrationEmailTemplate }) => {
  const history = useHistory();
  const { gacha } = useGacha();
  const [form] = Form.useForm<FormValues>();

  const isUpdate = history.location.pathname.endsWith('/update');

  return (
    <div>
      <Typography.Title>{isUpdate ? 'ガチャの更新' : 'ガチャの新規作成'}</Typography.Title>

      <Form
        labelCol={formItemLayout.labelCol}
        wrapperCol={formItemLayout.wrapperCol}
        form={form}
        labelAlign="left"
        onFinish={onFinish}
      >
        <>
          <Typography.Title level={3}>ガチャ新規作成時設定</Typography.Title>

          <Form.Item name="name" label="ガチャ名" rules={[{ required: true }]} initialValue={gacha?.name}>
            <Input disabled={isUpdate} />
          </Form.Item>

          {!isUpdate ? (
            <Form.Item
              name="encryptionPassword"
              label="ギフトコード暗号パスワード"
              rules={[{ required: true }]}
              initialValue=""
            >
              <Input type="password" disabled={isUpdate} />
            </Form.Item>
          ) : null}
        </>

        <>
          <Typography.Title level={3}>ガチャ管理設定</Typography.Title>

          <Form.Item
            name="upperTimes"
            label="ガチャの上限回数"
            initialValue={gacha?.upperTimes}
            rules={[{ required: true, type: 'number' }]}
          >
            <InputNumber />
          </Form.Item>

          <Form.Item label="当選確率（%）" required>
            <Form.Item
              name="winningPercentage"
              noStyle
              initialValue={gacha?.winningRate ? convertRateToPercentage(gacha?.winningRate) : undefined}
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: FORM_SETTINGS.winningPercentage.min,
                  max: FORM_SETTINGS.winningPercentage.max,
                },
              ]}
            >
              <InputNumber
                min={FORM_SETTINGS.winningPercentage.min}
                max={FORM_SETTINGS.winningPercentage.max}
                step={FORM_SETTINGS.winningPercentage.step}
              />
            </Form.Item>
            <span> %</span>
          </Form.Item>

          <Form.Item
            name="canRetry"
            label="引き直しを可能にする"
            valuePropName="checked"
            initialValue={gacha?.canRetry}
          >
            <Switch defaultChecked={gacha?.canRetry} />
          </Form.Item>

          <Form.Item label="引き直しまでの時間（時間）">
            <Form.Item name="retryInterval" noStyle initialValue={(gacha?.retryInterval ?? 0) / 60 / 60}>
              <InputNumber min={0} />
            </Form.Item>{' '}
            時間
          </Form.Item>

          {isUpdate ? (
            <Form.Item
              name="validPeriod"
              label="ガチャの有効期間"
              initialValue={[moment(gacha?.startTime), moment(gacha?.endTime)]}
              rules={[{ required: true }]}
            >
              <DatePicker.RangePicker showTime />
            </Form.Item>
          ) : (
            <Form.Item name="validPeriod" label="ガチャの有効期間" rules={[{ required: true }]}>
              <DatePicker.RangePicker
                showTime={{
                  defaultValue: [rangePickerDefaultTime, rangePickerDefaultTime],
                }}
              />
            </Form.Item>
          )}
        </>

        <>
          <Typography.Title level={3}>エンドユーザ向けガチャ情報</Typography.Title>

          <Form.Item name="gameTitle" label="タイトル" initialValue={gacha?.gameTitle}>
            <Input />
          </Form.Item>

          <Form.Item name="developerName" label="デベロッパー名" initialValue={gacha?.developerName}>
            <Input />
          </Form.Item>

          <Form.Item name="iconImageUrl" label="アイコン画像URL" initialValue={gacha?.iconImageUrl}>
            <Input />
          </Form.Item>

          <Form.Item name="gameReleaseTime" label="リリース時期" initialValue={gacha?.gameReleaseTime}>
            <Input />
          </Form.Item>

          <Form.Item name="giftDetail" label="ギフトコード内容" initialValue={gacha?.giftDetail}>
            <Input />
          </Form.Item>

          <Form.Item name="iosDownloadUrl" label="iOS用ダウンロードURL" initialValue={gacha?.iosDownloadUrl}>
            <Input />
          </Form.Item>

          <Form.Item
            name="androidDownloadUrl"
            label="Android用ダウンロードURL"
            initialValue={gacha?.androidDownloadUrl}
          >
            <Input />
          </Form.Item>

          <Form.Item name="articleUrl" label="紹介記事URL" initialValue={gacha?.articleUrl}>
            <Input />
          </Form.Item>

          <Form.Item name="officialSiteUrl" label="公式サイトURL" initialValue={gacha?.officialSiteUrl}>
            <Input />
          </Form.Item>

          <Form.Item name="gameIntroduction" label="紹介文" initialValue={gacha?.gameIntroduction}>
            <Input.TextArea rows={10} />
          </Form.Item>
        </>

        <>
          <Typography.Title level={3}>メール設定</Typography.Title>
          <Col xs={24} sm={17}>
            <Alert
              message="テキストと HTML の両方を入力してください"
              description={
                <Typography.Text>
                  HTMLメールが優先的に表示され、HTMLメールが表示できない場合にテキストメールが表示されるようになっています。
                  <br />
                  参考:{' '}
                  <Typography.Link
                    href="https://docs.aws.amazon.com/ja_jp/ses/latest/DeveloperGuide/send-email-concepts-email-format.html#send-email-concepts-email-format-body"
                    target="_blank"
                  >
                    E メール本文（E メール形式と Amazon SES）
                  </Typography.Link>
                </Typography.Text>
              }
              type="info"
              showIcon
            />
          </Col>
          <div style={{ marginBottom: '1rem' }} />

          <Typography.Title level={4}>事前登録完了メール</Typography.Title>

          <Form.Item
            name="registrationNotificationEmailTitle"
            label="件名"
            initialValue={registrationEmailTemplate?.title}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="registrationNotificationEmailBody"
            label={
              <div>
                <p>本文</p>
                【置換文字】
                <br />
                {'{{nickname}}'}: 登録者のユーザ名
                <br />
                {'{{mailAddress}}'}: 登録者のメアド
                <br />
                {'{{game}}'}: ガチャのゲームタイトル
              </div>
            }
            initialValue={registrationEmailTemplate?.body}
          >
            <Input.TextArea rows={10} />
          </Form.Item>

          <Form.Item
            name="registrationNotificationEmailHtmlBody"
            label={
              <div>
                <p>本文（HTML）</p>
                【置換文字】
                <br />
                {'{{nickname}}'}: 登録者のユーザ名
                <br />
                {'{{mailAddress}}'}: 登録者のメアド
                <br />
                {'{{game}}'}: ガチャのゲームタイトル
              </div>
            }
            initialValue={registrationEmailTemplate?.htmlBody}
          >
            <Input.TextArea rows={10} />
          </Form.Item>

          <Typography.Title level={4}>リリース通知メール</Typography.Title>

          <Form.Item name="releaseNotificationEmailTitle" label="件名" initialValue={releaseEmailTemplate?.title}>
            <Input />
          </Form.Item>

          <Form.Item
            name="releaseNotificationEmailBody"
            label={
              <div>
                <p>本文</p>
                【置換文字】
                <br />
                {'{{nickname}}'}: 登録者のユーザ名
                <br />
                {'{{mailAddress}}'}: 登録者のメアド
                <br />
                {'{{game}}'}: ガチャのゲームタイトル
                <br />
                {'{{downloadUrl}}'}: ゲームのダウンロードURL
              </div>
            }
            initialValue={releaseEmailTemplate?.body}
          >
            <Input.TextArea rows={10} />
          </Form.Item>

          <Form.Item
            name="releaseNotificationEmailHtmlBody"
            label={
              <div>
                <p>本文（HTML）</p>
                【置換文字】
                <br />
                {'{{nickname}}'}: 登録者のユーザ名
                <br />
                {'{{mailAddress}}'}: 登録者のメアド
                <br />
                {'{{game}}'}: ガチャのゲームタイトル
                <br />
                {'{{downloadUrl}}'}: ゲームのダウンロードURL
              </div>
            }
            initialValue={releaseEmailTemplate?.htmlBody}
          >
            <Input.TextArea rows={10} />
          </Form.Item>
        </>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            {isUpdate ? '更新' : '作成'}する
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default GachaForm;
